import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { academicCalendarService } from '../../../services/academicCalendarService';
import AcademicYearModal from './AcademicYearModal';
import SemesterModal from './SemesterModal'; // You'll need to create this or reuse logic
import ActionToast from '../../../components/ui/ActionToast';
import { useAuth } from '../../../contexts/AuthContext';

const AcademicCalendar = ({ onAddEvent, onNewYear, onYearSettings, refreshKey, onEditEvent, view = 'events', onRefresh }) => {
  const { profile } = useAuth();
  const canManageArchive = ['system_administrator', 'registrar_academic'].includes(profile?.primary_role);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedYear, setExpandedYear] = useState(null);
  const [semesters, setSemesters] = useState({}); // Map yearId -> semesters list
  const [events, setEvents] = useState([]); // Calendar events
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null);
  const [archiveSummaries, setArchiveSummaries] = useState({});

  // Modal states
  const [showYearModal, setShowYearModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [showSemesterModal, setShowSemesterModal] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [targetYearId, setTargetYearId] = useState(null); // For creating semester in a specific year

  useEffect(() => {
    fetchYears();
    if (view === 'events') fetchEvents();
  }, [refreshKey, view]);

  const fetchYears = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const data = await academicCalendarService.getAcademicYears();
      setAcademicYears(data || []);
      if (view === 'archive') {
        try {
          const summaries = await academicCalendarService.getArchiveSummaries();
          setArchiveSummaries(Object.fromEntries((summaries || []).map(item => [item.academic_year_id, item])));
        } catch (summaryError) {
          console.warn('Archive snapshots are not available yet', summaryError);
        }
      }

      // Auto-expand active year if not manually expanded yet
      if (!expandedYear) {
        const active = data?.find(y => y.status === 'active');
        if (active) {
          handleExpandYear(active.id);
        } else if (data && data.length > 0) {
          handleExpandYear(data[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to load academic years", error);
      setLoadError(error?.message || 'Academic archive could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const data = await academicCalendarService.getCalendarEvents();
      setEvents(data || []);
    } catch (error) {
      console.error("Failed to load events", error);
    }
  };

  const handleExpandYear = async (yearId) => {
    if (expandedYear === yearId) {
      setExpandedYear(null);
      return;
    }
    setExpandedYear(yearId);

    // Fetch semesters if not already loaded
    if (!semesters[yearId]) {
      try {
        const sems = await academicCalendarService.getSemesters(yearId);
        setSemesters(prev => ({ ...prev, [yearId]: sems }));
      } catch (error) {
        console.error("Failed to load semesters", error);
      }
    }
  };

  // Year CRUD
  const handleCreateYear = () => {
    setSelectedYear(null);
    setShowYearModal(true);
  };

  const handleEditYear = (year) => {
    setSelectedYear(year);
    setShowYearModal(true);
  };

  const handleArchiveYear = async (year) => {
    const reason = window.prompt(`Archive ${year.name}? Enter the closure/archiving reason:`);
    if (reason === null) return;
    if (reason.trim().length < 5) {
      setNotice({ type: 'error', message: 'Enter a meaningful archive reason of at least 5 characters.' });
      return;
    }
    try {
      await academicCalendarService.archiveAcademicYear(year.id, reason.trim());
      setSemesters(prev => ({ ...prev, [year.id]: undefined }));
      await fetchYears();
      setNotice({ type: 'success', message: `${year.name} was archived and locked successfully.` });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || 'The academic year could not be archived.' });
    }
  };

  const handleSaveYear = async (formData) => {
    try {
      if (selectedYear) {
        await academicCalendarService.updateAcademicYear(selectedYear.id, formData);
      } else {
        await academicCalendarService.createAcademicYear(formData);
      }
      setShowYearModal(false);
      fetchYears();
      setNotice({ type: 'success', message: selectedYear ? 'Academic year updated.' : 'Academic year created.' });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error saving year", error);
      setNotice({ type: 'error', message: error?.message || 'Academic year could not be saved.' });
    }
  };

  // Semester CRUD
  const handleAddSemester = (yearId) => {
    setTargetYearId(yearId);
    setSelectedSemester(null);
    setShowSemesterModal(true);
  };

  const handleEditSemester = (semester, yearId) => {
    setTargetYearId(yearId);
    setSelectedSemester(semester);
    setShowSemesterModal(true);
  };

  const handleDeleteSemester = async (id, yearId) => {
    if (window.confirm('Delete this semester?')) {
      try {
        await academicCalendarService.deleteSemester(id);
        // Refresh semesters for this year
        const sems = await academicCalendarService.getSemesters(yearId);
        setSemesters(prev => ({ ...prev, [yearId]: sems }));
      } catch (error) {
        alert('Error deleting semester');
      }
    }
  };

  const handleDeleteEvent = async (id) => {
    if (window.confirm('Delete this event?')) {
      try {
        await academicCalendarService.deleteCalendarEvent(id);
        fetchEvents();
      } catch (error) {
        alert('Error deleting event');
      }
    }
  };

  const handleSaveSemester = async (formData) => {
    try {
      if (selectedSemester) {
        await academicCalendarService.updateSemester(selectedSemester.id, formData);
      } else {
        await academicCalendarService.createSemester({ ...formData, academic_year_id: targetYearId });
      }

      // Refresh
      const sems = await academicCalendarService.getSemesters(targetYearId);
      setSemesters(prev => ({ ...prev, [targetYearId]: sems }));
      setShowSemesterModal(false);
      setNotice({ type: 'success', message: selectedSemester ? 'Teaching period updated.' : 'Teaching period added.' });

      // Also refresh years to update status if changed via trigger
      fetchYears();
    } catch (error) {
      console.error("Error saving semester", error);
      setNotice({ type: 'error', message: error?.message || 'Teaching period could not be saved.' });
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'archived': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'reporting': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'exam': return 'text-red-600 bg-red-50 border-red-200';
      case 'holiday': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };



  // ... (keep existing effects/functions)

  return (
    <div className="space-y-6">
      {/* Unified Tab Header */}
      {/* Dynamic Header based on View */}
      <div className="flex justify-between items-center bg-card p-4 rounded-lg border border-border">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            {view === 'events' ? 'Events Schedule' : 'Academic Archive'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {view === 'events'
              ? 'Manage key academic dates, exams, and holidays'
              : 'Review academic periods and permanently lock completed years'}
          </p>
        </div>
        <div>
          {view === 'events' ? (
            <Button variant="default" size="sm" onClick={onAddEvent}>
              <Icon name="Plus" size={14} className="mr-1" />
              Add Event
            </Button>
          ) : (
            canManageArchive ? <Button variant="default" size="sm" onClick={handleCreateYear}>
              <Icon name="Plus" size={14} className="mr-1" />
              New Academic Year
            </Button> : <span className="text-xs text-muted-foreground"><Icon name="Eye" size={14} className="mr-1 inline" />Read-only access</span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-xl">
          <Icon name="Loader2" size={32} className="mx-auto mb-2 animate-spin text-primary" />
          <p>Loading data...</p>
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-900" role="alert">
          <Icon name="AlertCircle" size={36} className="mx-auto mb-3" />
          <h4 className="font-semibold">Academic archive unavailable</h4>
          <p className="mt-1 text-sm">{loadError}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={fetchYears}>Retry</Button>
        </div>
      ) : (
        <>
          {/* Events Tab Content */}
          {view === 'events' && (
            <div className="bg-card border border-border rounded-xl p-6 min-h-[400px]">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-foreground">Upcoming Events</h3>
                <p className="text-sm text-muted-foreground">Key academic dates, exams, and holidays.</p>
              </div>
              {events.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Icon name="Calendar" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                  <h4 className="text-lg font-medium text-foreground mb-1">No events scheduled</h4>
                  <p className="text-sm mb-4">Get started by adding key dates to the calendar.</p>
                  <Button variant="outline" size="sm" onClick={onAddEvent}>
                    <Icon name="Plus" size={14} className="mr-1" />
                    Add Event
                  </Button>
                </div>
              ) : (
                <div className="relative border-l border-border ml-3 space-y-8 py-2 max-w-3xl">
                  {events.map((event) => (
                    <div key={event.id} className="ml-8 relative group">
                      {/* Dot on timeline */}
                      <div className={`absolute -left-[39px] top-1 w-5 h-5 rounded-full border-4 border-background ${event.event_type === 'exam' ? 'bg-red-500' : event.event_type === 'holiday' ? 'bg-emerald-500' : 'bg-blue-500'}`}></div>

                      <div className="flex flex-col sm:flex-row sm:items-start justify-between bg-muted/5 p-4 rounded-lg border border-border/50 hover:border-border transition-colors">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider font-semibold ${getEventTypeColor(event.event_type)}`}>
                              {event.event_type?.replace('_', ' ')}
                            </span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Icon name="Clock" size={12} />
                              {formatDate(event.start_date)} {event.end_date && `- ${formatDate(event.end_date)}`}
                            </span>
                          </div>
                          <h5 className="font-semibold text-base text-foreground">{event.title}</h5>
                          {event.description && <p className="text-sm text-muted-foreground mt-1">{event.description}</p>}
                        </div>
                        <div className="flex items-center gap-1 mt-3 sm:mt-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" onClick={() => onEditEvent && onEditEvent(event)}>
                            <Icon name="Edit" size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteEvent(event.id)}>
                            <Icon name="Trash" size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Archive Tab Content */}
          {view === 'archive' && (
            <div className="space-y-4">
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                <strong>Archive protection:</strong> completed academic years are snapshotted and permanently locked. Active or upcoming years must be closed before archiving.
              </div>
              {academicYears.length === 0 && <div className="text-center p-8 bg-muted/20 border rounded-xl">No academic years configured. Start by creating one.</div>}

              {academicYears.map((year) => (
                <div key={year.id} className={`bg-card border rounded-xl transition-all ${year.status === 'active' ? 'border-primary shadow-sm ring-1 ring-primary/20' : 'border-border'}`}>
                  {/* Year Header */}
                  <div
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 cursor-pointer hover:bg-muted/5 transition-colors"
                    onClick={() => handleExpandYear(year.id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${year.status === 'active' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        <Icon name="Calendar" size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-lg text-foreground">{year.name}</h4>
                          <span className={`text-xs px-2.5 py-0.5 rounded-full border ${getStatusColor(year.status)} capitalize font-medium`}>
                            {year.status}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-3">
                          <span className="flex items-center gap-1"><Icon name="Clock" size={12} /> {year.start_date} - {year.end_date}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                      {canManageArchive && year.status !== 'archived' && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditYear(year); }} title="Edit academic year">
                        <Icon name="Edit" size={14} />
                      </Button>}
                      {canManageArchive && year.status === 'inactive' && <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleArchiveYear(year); }} title="Archive and lock this year">
                        <Icon name="Lock" size={14} className="mr-1" /> Archive
                      </Button>}
                      <div className={`p-1 rounded-full hover:bg-muted transition-transform duration-200 ${expandedYear === year.id ? 'rotate-180' : ''}`}>
                        <Icon name="ChevronDown" size={20} className="text-muted-foreground" />
                      </div>
                    </div>
                  </div>

                  {/* Semesters List */}
                  {expandedYear === year.id && (
                    <div className="border-t border-border bg-muted/5 p-4 space-y-3">
                      {year.status === 'archived' && (
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                          <span className="font-semibold"><Icon name="ShieldCheck" size={15} className="mr-1 inline" />Locked archive</span>
                          <span>Archived {year.archived_at ? new Date(year.archived_at).toLocaleString() : ''}</span>
                          {year.archive_reason && <span>Reason: {year.archive_reason}</span>}
                          {archiveSummaries[year.id] && <span>Snapshot v{archiveSummaries[year.id].snapshot_version}</span>}
                        </div>
                      )}
                      <div className="flex justify-between items-center mb-2 px-1">
                        <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Semesters</h5>
                        {canManageArchive && year.status !== 'archived' && <Button variant="outline" size="sm" onClick={() => handleAddSemester(year.id)} className="h-8">
                          <Icon name="Plus" size={12} className="mr-1" /> Add Semester
                        </Button>}
                      </div>

                      {!semesters[year.id] || semesters[year.id].length === 0 ? (
                        <div className="text-center py-6 border-2 border-dashed border-muted rounded-lg">
                          <p className="text-sm text-muted-foreground italic">No semesters added yet.</p>
                          {canManageArchive && year.status !== 'archived' && <Button variant="link" size="sm" onClick={() => handleAddSemester(year.id)} className="mt-1">
                            Add First Semester
                          </Button>}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {semesters[year.id].map(sem => (
                            <div key={sem.id} className="bg-background border border-border rounded-lg p-3 flex justify-between items-center group hover:border-primary/30 hover:shadow-sm transition-all">
                              <div className="flex items-center gap-4">
                                <div className={`w-1.5 h-10 rounded-full ${sem.status === 'active' ? 'bg-green-500' : sem.status === 'upcoming' ? 'bg-blue-400' : 'bg-gray-300'}`}></div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-foreground">{sem.name}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border capitalize ${getStatusColor(sem.status)} font-medium`}>{sem.status}</span>
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                                    <span>{sem.start_date} to {sem.end_date}</span>
                                    <span className="text-border">|</span>
                                    <span>Term {sem.term_number}</span>
                                  </div>
                                </div>
                              </div>
                              {canManageArchive && year.status !== 'archived' && <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditSemester(sem, year.id)}>
                                  <Icon name="Edit" size={14} />
                                </Button>
                              </div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Reusing existing Modals or new ones */}
      {showYearModal && (
        <AcademicYearModal
          isOpen={showYearModal}
          onClose={() => setShowYearModal(false)}
          academicYear={selectedYear}
          onSave={handleSaveYear}
          mode={selectedYear ? 'edit' : 'new'}
        />
      )}

      {showSemesterModal && (
        <SemesterModal
          isOpen={showSemesterModal}
          onClose={() => setShowSemesterModal(false)}
          semester={selectedSemester}
          yearId={targetYearId}
          onSave={handleSaveSemester}
        />
      )}

      <ActionToast notice={notice} onClose={() => setNotice(null)} />

    </div>
  );
};

export default AcademicCalendar;
